/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { List, Map } from 'immutable';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import {
  useTheme, lighten, createTheme, ThemeProvider,
} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

/* ––
 * ––– Inner Components
 * –––––––––––––––––––––––––––––––– */
function CustomCalendarHeader({
  currentMonth, onMonthChange, locale, enabledDates, disabled,
}) {
  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const selectNextMonth = () => onMonthChange(currentMonth.plus({ months: 1 }), 'left');
  const selectPreviousMonth = () => onMonthChange(currentMonth.minus({ months: 1 }), 'right');

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  const isPreviousMonthDisabled = useMemo(
    () => (
      disabled || currentMonth.diff(
        DateTime.fromISO(enabledDates.first(), { zone: currentMonth.zoneName }),
      ).toMillis() < 0
    ),
    [disabled, currentMonth, enabledDates],
  );
  const isNextMonthDisabled = useMemo(
    () => (
      disabled || currentMonth.endOf('month').diff(
        DateTime.fromISO(enabledDates.last(), { zone: currentMonth.zoneName }),
      ).toMillis() > 0),
    [disabled, currentMonth, enabledDates],
  );

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
      <IconButton onClick={selectPreviousMonth} title="Previous month" disabled={isPreviousMonthDisabled} sx={{ fontSize: '2rem' }}>
        <ChevronLeft />
      </IconButton>
      <Typography variant="h3" fontWeight="medium">{currentMonth.setLocale(locale).toLocaleString({ month: 'long', year: 'numeric' })}</Typography>
      <IconButton onClick={selectNextMonth} title="Next month" disabled={isNextMonthDisabled} sx={{ fontSize: '2rem' }}>
        <ChevronRight />
      </IconButton>
    </Box>
  );
}

CustomCalendarHeader.propTypes = {
  currentMonth:  PropTypes.instanceOf(DateTime).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  locale:        PropTypes.string.isRequired,
  enabledDates:  PropTypes.instanceOf(List).isRequired,
  disabled:      PropTypes.bool,
};

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function DatePicker({
  calendarAvailability, selectedDate, onDateSelected, locale, disabled,
}) {
  /* –– Resources
   * –––––––––––––––––––––––––––––––– */
  const globalTheme = useTheme();

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const selectDate = (value) => {
    const selectedDayId = value.toISODate();

    const selectedDay = calendarAvailability.find((day) => day.get('id') === selectedDayId);

    if (selectedDay) {
      onDateSelected(selectedDay);
    }
  };

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  const bookingCalendarTheme = useMemo(() => createTheme({
    ...globalTheme,
    components: {
      MuiIconButton:   { styleOverrides: { root: { '&:disabled': { opacity: 0.5 } } } },
      MuiSvgIcon:      { styleOverrides: { root: { fontSize: 'inherit' } } },
      MuiDateCalendar: {
        styleOverrides: {
          root: ({ theme }) => ({
            width:     '320px',
            height:    'auto',
            maxHeight: '450px',

            [theme.breakpoints.up('md')]: { width: '100%' },

            '& .MuiDayCalendar-slideTransition':          { minHeight: '314px' },
            '& .MuiDateCalendar-viewTransitionContainer': { marginTop: '12px' },

            '& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel': {
              fontSize:   '1.075rem',
              fontWeight: theme.typography.fontWeightMedium,
              color:      theme.palette.text.primary,
              width:      '43px',
              height:     '50px',
            },
          }),
        },
      },
      MuiPickersDay: {
        defaultProps:   { disableRipple: true },
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.body1,
            fontWeight:   theme.typography.fontWeightMedium,
            borderRadius: 6,
            width:        '43px',
            height:       '50px',
            transition:   theme.transitions.create(
              ['background-color', 'border-color'],
              {
                duration: theme.transitions.duration.short,
                easing:   theme.transitions.easing.easeOut,
              },
            ),
            '&.MuiPickersDay-dayOutsideMonth': {
              color: theme.palette.text.disabled,
            },
            '&:not(.Mui-disabled):not(.Mui-selected)': {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: theme.palette.grey[700],
            },
            '&:not(.Mui-disabled):not(.Mui-selected):hover': {
              backgroundColor: lighten(theme.palette.primary.main, 0.9),
              borderColor:     theme.palette.primary.main,
            },
            '&.Mui-disabled:not(.Mui-selected):not(.MuiPickersDay-dayOutsideMonth)': {
              color: theme.palette.text.primary,
            },
            '&.Mui-selected': {
              color: theme.palette.primary.contrastText,
            },
            '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected:active': {
              backgroundColor: theme.palette.primary.main,
            },
          }),
        },
      },
    },
  }), [globalTheme]);

  const enabledDates = useMemo(
    () => calendarAvailability?.map((day) => day.get('id')) || new List(),
    [calendarAvailability],
  );

  return (
    <Box pt={{ xs: 3, md: 2 }}>
      <ThemeProvider theme={bookingCalendarTheme}>
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
          <DateCalendar
            view="day"
            views={['day']}
            fixedWeekNumber={6}
            showDaysOutsideCurrentMonth
            disableHighlightToday
            disabled={disabled}
            dayOfWeekFormatter={(date) => date.setLocale(locale).toLocaleString({ weekday: 'short' })}
            shouldDisableDate={(date) => !enabledDates.includes(date.toISODate())}
            value={selectedDate?.get('date')}
            onChange={selectDate}
            slots={{ calendarHeader: CustomCalendarHeader }}
            slotProps={{ calendarHeader: { locale, enabledDates } }}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </Box>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
DatePicker.propTypes = {
  calendarAvailability: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.oneOf([null])]),
  selectedDate:         PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.oneOf([null])]),
  onDateSelected:       PropTypes.func.isRequired,
  locale:               PropTypes.string.isRequired,
  disabled:             PropTypes.bool.isRequired,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default DatePicker;
